import { memo, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  useAdminEventsSubscription,
  useRoomEventsSubscription,
  useUserEventsSubscription,
} from "@/graphql/generated";
import { Role } from "@/types/role.enum";
import { updateCredentials } from "@/context/features/auth/authSlice";
import {
  setDocStat,
  setUpdatePatientLine,
  setvisitResults,
} from "@/context/features/room/roomSlice";
import { toaster } from "@/utils/toaster";
import { AdminStatus } from "@/types/public";

type Props = {
  role?: string | null;
  roomName: string;
  wsConnected: boolean;
  userId: string;
  handleJoinRequest: (d: { userId: string; fullName: string }) => void;
  runExitOnce: (
    toastType: "error" | "info" | "default",
    actionType: "kick" | "hold",
    messageKey: string
  ) => void;
};

function SubscriptionsMount({
  role,
  roomName,
  userId,
  wsConnected,
  handleJoinRequest,
  runExitOnce,
}: Props) {
  const dispatch = useDispatch();
  const startGate = Boolean(userId && roomName && wsConnected);

  const [start, setStart] = useState(false);
  useEffect(() => {
    if (!startGate) return setStart(false);
    const id = setTimeout(() => setStart(true), 0);
    return () => clearTimeout(id);
  }, [startGate]);

  useRoomEventsSubscription({
    variables: { roomName },
    skip: !start || !roomName,
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const event = subscriptionData.data?.roomEvents;
      if (!event) return;
      if (event.adminStatusUpdate) {
        dispatch(setDocStat(event.adminStatusUpdate.status as AdminStatus));
      }
      if (event.removeUpcomingUser) {
        dispatch(setUpdatePatientLine(true));
      }
      if (event.adminKickParticipant) {
        if (
          event.adminKickParticipant.userId === "all" &&
          String(role).toUpperCase() !== Role.HOST
        )
          runExitOnce("error", "kick", "kicked");
      }
    },
    onError: (e) => {
      console.error("adminEvents subscription error", e);
    },
  });
  useAdminEventsSubscription({
    variables: { roomName },
    skip: !start || !roomName || String(role).toUpperCase() !== Role.HOST,
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const event = subscriptionData.data?.adminEvents;
      if (!event) return;
      if (event.adminJoinRequest) {
        handleJoinRequest({
          userId: event.adminJoinRequest.userId,
          fullName: event.adminJoinRequest.fullName,
        });
      }
    },
    onError: (e) => {
      console.error("adminEvents subscription error", e);
    },
  });
  useUserEventsSubscription({
    variables: { userId },
    skip: !start || !userId || String(role).toUpperCase() === Role.HOST,
    shouldResubscribe: true,
    onSubscriptionData: ({ subscriptionData }) => {
      const event = subscriptionData.data?.userEvents;
      if (!event) return;

      if (event.adminAlertParticipant?.message) {
        toaster("info", event.adminAlertParticipant.message);
      }
      if (event.adminKickParticipant) {
        dispatch(updateCredentials({ approved: false }));
        if (event.adminKickParticipant.result) {
          const parsed = JSON.parse(event.adminKickParticipant.result);
          dispatch(setvisitResults({ ...parsed }));
        }
        runExitOnce("error", "kick", "kicked");
      }
      if (event.adminHoldParticipant) {
        runExitOnce("error", "hold", "you_have_been_put_on_hold");
      }
      if (event.adminReapproveParticipant) {
        dispatch(updateCredentials({ approved: false, isDone: false }));
      }
      if (event.adminJoinApprove) {
        dispatch(updateCredentials({ approved: true }));
      }
    },
    onError: (e) => {
      console.error("userEvents subscription error", e);
    },
  });

  return null;
}

export default memo(SubscriptionsMount);
